import { Skeleton, Tooltip } from "antd";
import React, { useEffect, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, useParams } from "react-router-dom";

import { ACTION_BTN_HOVER_CLR } from "@/Constants/common";
import {
  toggleComplianceModal,
  toggleEditLoading,
  updateSelectedDocuments,
} from "@/reducers/candidateCreate";
import { TOOLTIPTEXT } from "@/utilities/CommonText";
import { complianceListColumn } from "@/utilities/datatable/columnFunctions";
import { apiDecrypt } from "@/utilities/keyEncryption";
import { showCommonToast } from "@/utilities/utils";

import ActionIcons from "../common/client/ActionIcons";
import TableNoContent from "../common/TableNoContent";
import CandidateComplianceModal from "../modals/client/compliance/CandidateComplianceModal";

const CandidateComplianceForm = ({ defaultSection }) => {
  const [activeComplianceTab, setActiveComplianceTab] = useState("core");
  const [openedPopoverRow, setOpenedPopoverRow] = useState(null);
  const [data, setData] = useState([]);
  const [highlightedDocIds, setHighlightedDocIds] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [hasCleanedUrl, setHasCleanedUrl] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const {
    editData: candidateEditData,
    candidateComplianceDocuments,
    selectedCandidateComplianceDocuments,
  } = useSelector((state) => state.candidateCreate);
  const editCandidateLoading = useSelector(
    (state) => state.candidateCreate.editLoading,
  );

  const [toggleListingSelection, setToggleListingSelection] = useState(false);
  const candidateId =
    candidateEditData?.candidate_id || (id ? apiDecrypt(id) : undefined);
  const typeList = [
    {
      name: "Core",
      type: "core",
    },
    {
      name: "Non-Core",
      type: "non_core",
    },
    {
      name: "Internal",
      type: "internal",
    },
    {
      name: "Enhanced",
      type: "client_enhance_compliance",
    },
  ];

  // Set active tab based on defaultSection prop and get highlighted docs
  useEffect(() => {
    if (defaultSection) {
      // Map the section name from URL to the actual type used in the component
      const sectionMapping = {
        core: "core",
        "non-core": "non_core",
        internal: "internal",
        enhanced: "client_enhance_compliance",
      };

      const mappedSection = sectionMapping[defaultSection];
      if (mappedSection) {
        setActiveComplianceTab(mappedSection);
      }

      // Get remaining compliance IDs from session storage
      const redirectContext = sessionStorage.getItem("proposeRedirect");
      if (redirectContext) {
        try {
          const context = JSON.parse(redirectContext);

          // Check if redirect is recent (within last 30 seconds)
          const isRecent = Date.now() - context.timestamp < 30000;

          if (isRecent && context.remainingEnhancedIds) {
            // Parse the comma-separated IDs
            const idsArray = context.remainingEnhancedIds
              .split(",")
              .map((id) => id.trim());
            setHighlightedDocIds(idsArray);

            console.log("Highlighting document IDs:", idsArray);
          }
        } catch (e) {
          console.error("Error parsing redirect context:", e);
        }
      }
    }
  }, [defaultSection]);

  // Handle initial loading state and clean URL after data is loaded
  useEffect(() => {
    if (
      candidateComplianceDocuments &&
      Object.keys(candidateComplianceDocuments).length > 0
    ) {
      setIsInitialLoading(false);

      // Clean URL parameters after loading is complete
      if (!hasCleanedUrl && defaultSection) {
        // Wait a bit to ensure everything is rendered
        setTimeout(() => {
          // Remove query parameters but keep the path
          const cleanPath = location.pathname;
          navigate(cleanPath, { replace: true });
          setHasCleanedUrl(true);
        }, 1000); // 1 second delay to ensure user sees the highlighted items
      }
    }
  }, [candidateComplianceDocuments, hasCleanedUrl, defaultSection]);

  useEffect(() => {
    if (
      candidateComplianceDocuments &&
      candidateComplianceDocuments[activeComplianceTab]
    ) {
      setData(candidateComplianceDocuments[activeComplianceTab]);
    }
  }, [candidateComplianceDocuments, activeComplianceTab]);

  const columns = [
    complianceListColumn.title(
      true,
      setOpenedPopoverRow,
      openedPopoverRow,
      candidateId,
    ),
    complianceListColumn.uploaded(true),
    complianceListColumn.approvedBy,
    complianceListColumn.dateOfUpdate,
    complianceListColumn.expiryDate,
  ];

  const wrapper = useRef(null);
  let height = wrapper?.current?.clientHeight
    ? wrapper?.current?.clientHeight - 66
    : 400;
  const dataTableHeight = `${height}px`;

  const setLoading = (value) => {
    dispatch(toggleEditLoading(value));
  };

  // Custom row styles to highlight missing documents
  const conditionalRowStyles = [
    {
      when: (row) => highlightedDocIds.includes(String(row.document_id)),
      style: {
        backgroundColor: "#FFF4E5",
        "&:hover": {
          backgroundColor: "#FFE0B2",
        },
      },
    },
  ];

  // Combined loading state
  const isLoading = editCandidateLoading || isInitialLoading;

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap gap-4">
        <div className="flex flex-wrap gap-3">
          {typeList.map((item, index) => {
            return (
              <div
                className={`shiftperiod_item flex h-[40px] min-w-[99px] cursor-pointer select-none items-center justify-center rounded-lg p-3 text-base font-medium transition-all duration-200 ease-linear ${
                  activeComplianceTab === item.type
                    ? "bg-lightblue text-primary"
                    : "bg-grey text-txtColor"
                }`}
                onClick={() => {
                  setActiveComplianceTab(item.type);
                  setToggleListingSelection(!toggleListingSelection);
                }}
                key={index}
              >
                {item.name}
              </div>
            );
          })}
        </div>
        <div className="flex h-[40px] cursor-pointer overflow-hidden rounded-lg bg-grey [&>*:last-child]:border-r-0 [&>*]:grid [&>*]:min-w-[56px] [&>*]:place-items-center [&>*]:border-0 [&>*]:border-r [&>*]:border-r-[#E5E6E9] [&>*]:text-txtColor">
          <Tooltip title={TOOLTIPTEXT.edit}>
            <div
              className={`group ${ACTION_BTN_HOVER_CLR.lightBlue}`}
              onClick={() => {
                if (selectedCandidateComplianceDocuments.length === 1) {
                  dispatch(toggleComplianceModal(true));
                } else {
                  showCommonToast("Please select one compliance ", {
                    id: "no-user-selected",
                  });
                }
              }}
            >
              <ActionIcons isLoading={isLoading} type={"edit"} />
            </div>
          </Tooltip>
        </div>
      </div>
      <div
        className="ComplianceListing_Wrapper DataTable_Wrapper min-h-[500px]"
        ref={wrapper}
      >
        <DataTable
          columns={columns}
          data={data}
          selectableRows
          clearSelecte1dRows={toggleListingSelection}
          onSelectedRowsChange={(state) => {
            let selectedDocuments = state.selectedRows.map((item) => {
              return item.document_id;
            });
            dispatch(updateSelectedDocuments(selectedDocuments));
          }}
          noDataComponent={<TableNoContent />}
          progressPending={isLoading}
          selectableRowsSingle
          progressComponent={<Skeleton active paragraph={{ rows: 1 }} />}
          fixedHeader
          fixedHeaderScrollHeight={dataTableHeight}
          conditionalRowStyles={conditionalRowStyles}
        />
      </div>
      <CandidateComplianceModal loading={isLoading} setLoading={setLoading} />
    </div>
  );
};

export default CandidateComplianceForm;
